import React, { useState, useEffect } from "react";
import api from "../utils/api";

const AnalyticsDrilldownModal = ({ isOpen, onClose, title, type, month, employeeName, filters }) => {
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState([]);

    useEffect(() => {
        if (isOpen && (month || employeeName)) {
            fetchDrilldownData();
        }
    }, [isOpen, month, employeeName]);

    const fetchDrilldownData = async () => {
        setLoading(true);
        try {
            const res = await api.get("/ticket/getAnalyticsDrilldown", {
                params: { type, month, employeeName, ...filters }
            });
            if (res.data.status) {
                setTickets(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching drilldown data:", err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4 border-b pb-2">
                            <h3 className="text-xl leading-6 font-bold text-gray-900" id="modal-title">
                                {title} ({tickets.length})
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center py-20">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                            </div>
                        ) : tickets.length === 0 ? (
                            <div className="py-10 text-center text-gray-500 font-medium">No tickets found for this selection.</div>
                        ) : (
                            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto custom-scrollbar">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ticket ID</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Involved Employees</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Duration</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Longest Stay At</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {tickets.map((ticket) => (
                                            <tr key={ticket.id} className="hover:bg-gray-50 transition border-b border-gray-50">
                                                <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-red-600">#{ticket.id}</td>
                                                <td className="px-4 py-4 text-sm text-gray-700 font-medium">{ticket.customer_name}</td>
                                                <td className="px-4 py-4 text-sm text-gray-600 max-w-sm break-words" title={ticket.title}>{ticket.title}</td>
                                                <td className="px-4 py-4 text-sm text-gray-600">
                                                    <div className="flex flex-wrap gap-1">
                                                        {ticket.employees.map(emp => (
                                                            <span key={emp.user_id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 mb-0.5">
                                                                {(emp.first_name || emp.last_name) ? `${emp.first_name} ${emp.last_name}`.trim() : (emp.user_name || `User #${emp.user_id}`)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                    {ticket.total_days} {ticket.total_days !== '-' ? 'days' : ''}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-600 font-medium italic">
                                                    {ticket.longest_held_by || 'N/A'}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ticket.status === 'Closed' ? 'bg-green-100 text-green-800' :
                                                        ticket.status === 'Reopen' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {ticket.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDrilldownModal;
